import { Skeleton } from '@mui/material';
import Constants from 'js/Constants';
import {
  deselectCard,
  resetPageNumbers,
  selectCard,
  setCurrentPage,
  setSortCriteria,
  setTotalItems,
  setTotalPages,
  updateSearchFilters,
} from 'js/actions';
import SearchBar from 'js/components/MacroInput/SearchBar';
import { formatAll, resultToComplex } from 'js/components/MacroInput/tools/macroTools';
import { segmentsToString } from 'js/components/MacroInput/tools/segmentsToString';
import { AvailableFilters } from 'js/components/MacroInput/types/AvailableFilters';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import CardDetailView from 'js/components/elements/cards/CardDetailView';
import CardsListPageNumbersContainer from 'js/components/elements/cards/CardsListPageNumbersContainer';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  listSelectStyles,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import UnsortedSelect from 'js/components/elements/rselect/UnsortedSelect';
import { DefaultFiltersByView, SearchFilterViewsType } from 'js/constants/visibilityFilters';
import { MacroOptionsContextWrap } from 'js/contexts/MacroOptionsContext';
import { areSegmentsDifferent } from 'js/lib/SearchFilterUtils';
import { find, isEmpty, isNil, pick, toInteger, trim, values } from 'lodash';
import DataCard, { DataCardCell } from 'new/elements/DataCard';
import React, { memo, ReactNode, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import StyledFixedPanel from 'new/components/StyledFixedPanel';
import styles from './DataCardList.module.scss';
import { useHandleAccessLevelChanges } from 'new/hooks/useHandleAccessLevelChanges';
import { useLocalAccessLevel } from 'new/hooks/useLocalAccessLevel';

type LoadData = (
  page: number,
  sortCriteria: string,
  availableFilters: AvailableFilters,
  options?: { aclQueryMode?: string },
) => (() => void) | Promise<{ totalPages: number; totalItems: number }>;

export type DataCardListProps = {
  data: any[];
  view: SearchFilterViewsType;
  searchPlaceholder?: string;
  availableFilterTypes: string[];
  urlId?: string;
  loading: boolean;
  disablePageHistoryUpdate?: boolean;
  /**
   * If true, the search filters will not be updated from the url params.
   */
  preventSearchFiltersUpdateFromUrlParams?: boolean;
  inModal?: boolean;
  isClientSideSearch?: boolean;
  noContentView?: ReactNode;
  detailViewTabs?: any[];
  detailHeaderContent?: any;
  enableSorting?: boolean;
  enablePagination?: boolean;
  rowRenderer: (index: number, item: any) => ReactNode;
  getSourceMeta?: (id: number) => any;
  loadData?: LoadData;
};

const sortCriteriaOptions = values(Constants.SortCriteriaInfo);

const ListSkeleton = () => (
  <div className={styles.list}>
    {new Array(9).fill(0).map((i, n) => (
      <DataCard
        key={n}
        disabled
        rowsSlots={[
          <>
            <DataCardCell>
              <Skeleton animation="wave" variant="rounded" width={40} height={40} />
            </DataCardCell>
            <DataCardCell nameDescCell>
              <Skeleton animation="wave" variant="rounded" width={300} height={20} />
            </DataCardCell>
            <DataCardCell rightCTAsCell>
              <Skeleton animation="wave" variant="rounded" width={150} height={40} />
            </DataCardCell>
          </>,
          <DataCardCell multiCol>
            <Skeleton animation="wave" variant="rounded" width={300} height={20} />
            <Skeleton animation="wave" variant="rounded" width={300} height={20} />
          </DataCardCell>,
        ]}
      />
    ))}
  </div>
);

const listingDefaults = {
  currentPage: 1,
  totalPages: null,
  totalItems: null,
  sortCriteria: 'LAST_CREATED_DESC',
};

function reducer(state, action) {
  switch (action.type) {
    case 'updateCurrentPage':
      return { ...state, currentPage: action.value };
    case 'updateTotalPages':
      return { ...state, totalPages: action.value };
    case 'updateTotalItems':
      return { ...state, totalItems: action.value };
    case 'updateSortCriteria':
      return { ...state, sortCriteria: action.value };
  }
  throw Error('Unknown action: ' + action.type);
}

const DataCardList = ({
  data,
  view,
  inModal = false,
  searchPlaceholder,
  availableFilterTypes,
  urlId,
  loading,
  isClientSideSearch,
  noContentView,
  detailViewTabs,
  detailHeaderContent,
  enableSorting = true,
  enablePagination = true,
  rowRenderer,
  disablePageHistoryUpdate = false,
  preventSearchFiltersUpdateFromUrlParams = false,
  getSourceMeta,
  loadData,
}: DataCardListProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [state, listingDispatch] = useReducer(reducer, listingDefaults);
  const { aclQueryMode: localAclQueryMode, ...accessLevelControlProps } = useLocalAccessLevel();
  const { filters: allFilters, availableFilters: allAvailableFilters } = useSelector(
    (state: any) => state.app.visibilityFilters,
  );
  const {
    aclQueryMode,
    currentPage: page,
    sortCriteria,
    selectedCardId,
  } = useSelector((state: any) => state.app.listing);
  const renderer = useMemo(() => rowRenderer, [rowRenderer]);
  const filters = allFilters[view];

  const currentSortCriteria = inModal ? state.sortCriteria : sortCriteria;
  const currentPage = inModal ? state.currentPage : page;

  const prevFilters = useRef();
  const isMountEffect = useRef(true);
  const { current: defaultFilters } = useRef(trim(segmentsToString(DefaultFiltersByView[view])));
  const currentFilters = useMemo(() => trim(segmentsToString(filters)), [filters]);
  const multiResourceList = !urlId; // treats 0, '', null, undefined
  const selectedCardData = useMemo(
    () => find(data, { id: selectedCardId }),
    [data, selectedCardId],
  );
  const selectedSortOption = getCurrentSelectedOption(sortCriteriaOptions, currentSortCriteria);
  const urlParams = new URLSearchParams(history.location.search);
  const pageParam =
    !disablePageHistoryUpdate && !inModal ? toInteger(urlParams.get('page')) : undefined;
  const searchParam = urlParams.get('search');

  const loadListData = (page = currentPage) => {
    const availableFilters = pick(allAvailableFilters, availableFilterTypes);

    if (loadData) {
      /**
       * If the list is in modal, we need to pass the localAclQueryMode to the
       * loadData function. Otherwise, we don't need to pass anything.
       * It'll be listening to the app.accessControl.mode
       */
      const options = inModal ? { aclQueryMode: localAclQueryMode } : {};

      const action = loadData(page, currentSortCriteria, availableFilters, options) as any;

      if (typeof action?.then !== 'function') return;

      action.then((response: { totalPages: number; totalItems: number }) => {
        if (!response?.totalItems && !response?.totalPages) return;

        const { totalPages, totalItems } = response;

        if (inModal) {
          listingDispatch({ type: 'updateTotalPages', value: totalPages });
          listingDispatch({ type: 'updateTotalItems', value: totalItems });
        } else {
          dispatch(setTotalPages(totalPages));
          dispatch(setTotalItems(totalItems));
        }
      });
    }
  };

  /**
   * registering the listening hook for access level changes
   */
  useHandleAccessLevelChanges(loadListData, [localAclQueryMode]);

  const onResourceSuggestionSelected = useCallback((id) => {
    dispatch(selectCard(id));
  }, []);

  const onCardDeselected = useCallback(() => {
    dispatch(deselectCard());
  }, []);

  const onSortCriteriaChanged = (selectedOption) => {
    const value = getNewSelectionValue(selectedOption, true, '');
    if (!inModal) {
      dispatch(setSortCriteria(value));
    } else {
      listingDispatch({ type: 'updateSortCriteria', value });
    }
  };

  const onListingChange = (action) => {
    listingDispatch(action);
  };

  useEffect(() => {
    if (!disablePageHistoryUpdate) {
      dispatch(resetPageNumbers());
    }

    if (searchParam && !isClientSideSearch && !preventSearchFiltersUpdateFromUrlParams) {
      dispatch(updateSearchFilters(view, resultToComplex(formatAll(searchParam)).segments));
    }
    if (pageParam && enablePagination) {
      dispatch(setCurrentPage(pageParam));
    }
  }, []);

  useEffect(() => {
    // don't load the list data during the first mount if search/pagination
    // query params are set. wait for Redux state updates in the mount effect to
    // trigger this effect.
    if (!(isMountEffect.current && pageParam && pageParam !== 1)) {
      loadListData();
    }

    isMountEffect.current = false;
  }, [currentPage, currentSortCriteria]);

  useEffect(() => {
    if (filters && prevFilters.current && areSegmentsDifferent(filters, prevFilters.current)) {
      loadListData();
    }

    prevFilters.current = filters;
  }, [filters]);

  const enableSearchBar = multiResourceList && !isEmpty(availableFilterTypes);

  return (
    <>
      <div className={styles.list}>
        <div className={styles.controlsContainer}>
          {enableSearchBar && (
            <div className={styles.innerControlsContainer}>
              <MacroOptionsContextWrap>
                <SearchBar
                  view={view}
                  inModal={inModal}
                  placeholder={searchPlaceholder}
                  availableFilterTypes={availableFilterTypes}
                  getSourceMeta={getSourceMeta}
                  isClientSideSearch={isClientSideSearch}
                  onResourceSelected={onResourceSuggestionSelected}
                  accessLevelControlProps={{
                    controlled: inModal,
                    ...accessLevelControlProps,
                  }}
                />
              </MacroOptionsContextWrap>
            </div>
          )}

          {multiResourceList && (
            <>
              {enableSorting && (
                <UnsortedSelect
                  options={sortCriteriaOptions}
                  components={{ Input: SelectInput, Option: SelectOption }}
                  isClearable={false}
                  isSearchable={true}
                  backspaceRemovesValue={false}
                  value={selectedSortOption}
                  onChange={onSortCriteriaChanged}
                  styles={listSelectStyles}
                />
              )}
              {enablePagination && (
                <CardsListPageNumbersContainer
                  listing={inModal ? state : undefined}
                  disableHistoryUpdate={disablePageHistoryUpdate}
                  onListingChange={inModal ? onListingChange : undefined}
                  isLoading={loading}
                />
              )}
            </>
          )}
        </div>

        {isEmpty(data) ? (
          loading ? (
            <ListSkeleton />
          ) : currentFilters && currentFilters !== defaultFilters ? (
            <NoContentTextView title={<div>No results match your search criteria.</div>} />
          ) : (
            noContentView
          )
        ) : (
          data.map((item, index) => renderer(index, item))
        )}
      </div>

      {selectedCardId > 0 && selectedCardData && !inModal && (
        <div>
          <div className="modal-fader" onClick={onCardDeselected} />
          <StyledFixedPanel>
            <CardDetailView
              data={selectedCardData}
              tabs={detailViewTabs}
              noHeaderPadding
              detailHeaderContent={detailHeaderContent}
              getSourceMeta={getSourceMeta}
              aclQueryMode={aclQueryMode}
            />
          </StyledFixedPanel>
        </div>
      )}
    </>
  );
};

export default memo(DataCardList);
